import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Eye, Users, Target, TrendingUp, ShieldCheck, Mail, Phone, ArrowRight, Handshake, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { SponsorGrid } from '../components/SponsorGrid';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const getbenefits = () => [
  {
    icon: Eye,
    title: t('pg.BliSponsor.dc0bad'),
    description:
      t('pg.BliSponsor.731df9'),
  },
  {
    icon: Heart,
    title: t('pg.BliSponsor.5ad340'),
    description:
      t('pg.BliSponsor.9f5bd4'),
  },
  {
    icon: ShieldCheck,
    title: t('pg.BliSponsor.45c17b'),
    description:
      t('pg.BliSponsor.3cb77f'),
  },
  {
    icon: TrendingUp,
    title: t('pg.BliSponsor.62dcba'),
    description:
      t('pg.BliSponsor.2fe5cd'),
  },
  {
    icon: Users,
    title: t('pg.BliSponsor.bd48fb'),
    description:
      t('pg.BliSponsor.237133'),
  },
];

export const BliSponsor = () => {
  useTranslation();
  return (
    <div data-testid="bli-sponsor-page">
      <SEO
        title={t('pg.BliSponsor.6aba99')}
        description={t('pg.BliSponsor.4ece52')}
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=1920&q=80"
            alt={t('pg.BliSponsor.b6620e')}
            loading="eager"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4 flex items-center gap-2">
            <Handshake className="w-4 h-4" /> {t('pg.BliSponsor.d2106b')}
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('pg.BliSponsor.aadc3b')}<br />
            <span className="text-green-400">{t('pg.BliSponsor.7c45b2')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mb-8">
            {t('pg.BliSponsor.66faa3')}
          </p>
          <a href="#kontakt-sponsor">
            <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-600 shadow-lg">
              {t('pg.BliSponsor.7d284a')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Intro – Mer enn en idrettsklubb */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <p className="text-green-500 font-medium uppercase tracking-widest mb-4 text-center">
            {t('pg.BliSponsor.7e3275')}
          </p>
          <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-8 text-center">
            {t('pg.BliSponsor.1af271')}
          </h2>

          <div className="space-y-6 text-slate-600 dark:text-slate-300 leading-relaxed text-lg">
            <p>
              {t('pg.BliSponsor.47b411')}{' '}
              <strong className="text-slate-900 dark:text-white">{t('pg.BliSponsor.9c4af7')}</strong>{t('pg.BliSponsor.70ff11')}
            </p>
            <p>
              {t('pg.BliSponsor.2cc881')}{' '}
              <strong className="text-slate-900 dark:text-white">{t('pg.BliSponsor.b68c25')}</strong>{t('pg.BliSponsor.d21375')}
            </p>
          </div>
        </div>
      </section>

      {/* Hvorfor bli sponsor */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">
              {t('pg.BliSponsor.b7acef')}
            </p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.BliSponsor.3cfa12')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pg.BliSponsor.abbbdc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getbenefits().map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 hover:border-green-500 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-green-500" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {b.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 bg-green-500/10 border border-green-500/30 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" aria-hidden="true" />
              <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                {t('pg.BliSponsor.4fab37')}{' '}
                <strong className="text-slate-900 dark:text-white">{t('pg.BliSponsor.3a802d')}</strong>{' '}
                {t('pg.BliSponsor.0ad539')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Våre nåværende samarbeidspartnere */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.BliSponsor.6c2803')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.BliSponsor.987f52')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {t('pg.BliSponsor.25d5c7')}
            </p>
          </div>

          <SponsorGrid />

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-5 py-2">
              <Sparkles className="w-4 h-4 text-green-500" aria-hidden="true" />
              <p className="text-slate-700 dark:text-slate-200 text-sm font-medium">
                {t('pg.BliSponsor.9d4deb')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bli med på laget – Kontakt */}
      <section id="kontakt-sponsor" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">
              {t('pg.BliSponsor.bf0185')}
            </p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-6">
              {t('pg.BliSponsor.677b98')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-lg max-w-2xl mx-auto">
              {t('pg.BliSponsor.070e89')}
            </p>
            <p className="text-slate-700 dark:text-slate-200 text-lg font-medium mt-4">
              {t('pg.BliSponsor.5fdc5c')} <span className="text-green-500">{t('pg.BliSponsor.2a1be2')}</span>{' '}
              {t('pg.BliSponsor.831770')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="mailto:nickolas.paynter@veitvet-sportsklubb.no"
              data-testid="sponsor-email-link"
              className="group bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 hover:border-green-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 transition-colors">
                  <Mail className="w-6 h-6 text-green-500 group-hover:text-white transition-colors" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider mb-1">
                    {t('pg.BliSponsor.3d4125')}
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold break-all">
                    nickolas.paynter@veitvet-sportsklubb.no
                  </p>
                </div>
              </div>
            </a>

            <a
              href="tel:+4790739749"
              data-testid="sponsor-phone-link"
              className="group bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 hover:border-green-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 transition-colors">
                  <Phone className="w-6 h-6 text-green-500 group-hover:text-white transition-colors" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider mb-1">
                    {t('pg.BliSponsor.0975cf')}
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold text-lg">
                    +47 907 39 749
                  </p>
                </div>
              </div>
            </a>
          </div>

          <div className="mt-12 text-center">
            <Link to="/contact">
              <Button variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white uppercase tracking-wider px-8 py-6 hover:bg-slate-100 dark:hover:bg-slate-700">
                {t('pg.BliSponsor.f59458')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default BliSponsor;
